import {
    AccountCategory,
    type AccountSummary,
    type HolisticSummaryCategory,
    LinkState,
    Provider,
    SpendAccountCategory,
} from '@fruitful/types';
import { formatDistanceToNowStrict } from 'date-fns';
import { capitalize } from 'lodash';
import * as React from 'react';
import { useState } from 'react';
import {
    BodyMediumEmphasized,
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    CloseButton,
    EditIcon,
    FootnoteRegular,
    Grid2x2Icon,
    H2Regular,
    HolisticAccountLogo,
    isWeb,
    KeyboardCompatibleBottomSheet,
    KeyboardCompatibleBottomSheetTextInput,
    LinkIcon,
    Private,
    RadioInputRow,
    StyledH2Input,
    TrashIcon,
    View,
    YStack,
} from 'smoothie';

import { FRUITFUL_INSTITUTION_NAME } from '../../banking/types/BankAccount';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import {
    formatMaskedValue,
    getPrettyAccountType,
} from '../../common/utils/bankingUtils';
import { isUnknownAccountType } from '../../common/utils/holisticUtils';
import { captureMessage } from '../../logger/Logger';
import {
    AccountCategoryIcons,
    SpendCategoryIcons,
} from '../types/HolisticSummary';

export enum EditSheetPage {
    EDIT = 'edit',
    RENAME = 'rename',
    RECATEGORIZE = 'recategorize',
    RECATEGORIZE_SPEND = 'recategorizeSpend',
    CONFIRM_HIDE = 'confirmHide',
}

interface EditAccountSheetProps {
    account: AccountSummary;
    page?: EditSheetPage;
    setPage: (page: EditSheetPage) => void;
    onClose: () => void;
    onSubmitRename?: (
        accountId: string,
        provider: Provider,
        rename: string,
    ) => Promise<void>;
    onSubmitRecategorizeAccount?: (
        account: AccountSummary,
        holisticCategory: HolisticSummaryCategory,
        spendAccountCategory?: SpendAccountCategory,
    ) => Promise<void>;
    onSubmitHideAccount: (
        accountId: string,
        provider: Provider,
        isLastAccountOnLink: boolean,
    ) => Promise<void>;
    onPressRelink: () => void;
}

export const EditAccountSheet = ({
    account,
    page = EditSheetPage.EDIT,
    setPage,
    onClose,
    onSubmitRename,
    onSubmitRecategorizeAccount,
    onSubmitHideAccount,
    onPressRelink,
}: EditAccountSheetProps) => {
    const user = useAuthedUser();
    const {
        id,
        provider,
        institutionName,
        isLastAccountOnLink = false,
    } = account;

    const [category, setCategory] = useState<HolisticSummaryCategory | null>(
        null,
    );

    const onPressRename = () => {
        setPage(EditSheetPage.RENAME);
    };

    const onPressRecategorize = () => {
        setPage(EditSheetPage.RECATEGORIZE);
    };

    let content: React.JSX.Element;

    switch (page) {
        case EditSheetPage.RENAME:
            if (onSubmitRename == null) {
                content = (
                    <EditAccount
                        account={account}
                        currentUserId={user.id}
                        onClose={onClose}
                        onPressRelink={onPressRelink}
                        onPressRecategorize={
                            onSubmitRecategorizeAccount != null
                                ? onPressRecategorize
                                : undefined
                        }
                        onPressHide={() => {
                            setPage(EditSheetPage.CONFIRM_HIDE);
                        }}
                    />
                );
                break;
            }
            content = (
                <RenameAccount
                    account={account}
                    onClose={onClose}
                    onSubmit={async (rename: string) => {
                        await onSubmitRename(id, provider, rename);
                        onClose();
                    }}
                />
            );
            break;
        case EditSheetPage.RECATEGORIZE:
            if (onSubmitRecategorizeAccount == null) {
                content = (
                    <EditAccount
                        account={account}
                        currentUserId={user.id}
                        onClose={onClose}
                        onPressRename={
                            onSubmitRename != null ? onPressRename : undefined
                        }
                        onPressRelink={onPressRelink}
                        onPressHide={() => {
                            setPage(EditSheetPage.CONFIRM_HIDE);
                        }}
                    />
                );
                break;
            }
            content = (
                <RecategorizeAccount
                    account={account}
                    onClose={onClose}
                    onSubmit={async (category: HolisticSummaryCategory) => {
                        if (
                            category === AccountCategory.SPEND &&
                            account.provider !== Provider.MANUAL &&
                            isUnknownAccountType(account.type)
                        ) {
                            // We need to specify if the account is cash or credit, and we can't infer it based on the missing account type
                            setPage(EditSheetPage.RECATEGORIZE_SPEND);
                            setCategory(category);
                        } else {
                            // Otherwise, we're good to directly update category
                            await onSubmitRecategorizeAccount(
                                account,
                                category,
                            );
                            onClose();
                        }
                    }}
                />
            );
            break;
        case EditSheetPage.RECATEGORIZE_SPEND:
            if (onSubmitRecategorizeAccount == null) {
                content = (
                    <EditAccount
                        account={account}
                        currentUserId={user.id}
                        onClose={onClose}
                        onPressRename={
                            onSubmitRename != null ? onPressRename : undefined
                        }
                        onPressRelink={onPressRelink}
                        onPressHide={() => {
                            setPage(EditSheetPage.CONFIRM_HIDE);
                        }}
                    />
                );
                break;
            }
            content = (
                <RecategorizeSpend
                    onClose={onClose}
                    onSubmit={async (
                        spendAccountCategory: SpendAccountCategory,
                    ) => {
                        if (category != null) {
                            await onSubmitRecategorizeAccount(
                                account,
                                category,
                                spendAccountCategory,
                            );
                            onClose();
                        } else {
                            captureMessage(
                                'Unexpected missing category when submitting spend account recategorization',
                            );
                            setPage(EditSheetPage.RECATEGORIZE);
                        }
                    }}
                />
            );
            break;
        case EditSheetPage.CONFIRM_HIDE:
            content = (
                <ConfirmHide
                    isLastAccountOnLink={isLastAccountOnLink}
                    institutionName={institutionName}
                    onConfirmHide={async () => {
                        await onSubmitHideAccount(
                            id,
                            provider,
                            isLastAccountOnLink,
                        );
                        onClose();
                    }}
                    onGoBack={() => {
                        account.linkState === LinkState.ERROR
                            ? onClose()
                            : setPage(EditSheetPage.EDIT);
                    }}
                />
            );
            break;
        case EditSheetPage.EDIT:
        default:
            content = (
                <EditAccount
                    account={account}
                    currentUserId={user.id}
                    onClose={onClose}
                    onPressRename={
                        onSubmitRename != null ? onPressRename : undefined
                    }
                    onPressRelink={onPressRelink}
                    onPressRecategorize={
                        onSubmitRecategorizeAccount != null
                            ? onPressRecategorize
                            : undefined
                    }
                    onPressHide={() => {
                        setPage(EditSheetPage.CONFIRM_HIDE);
                    }}
                />
            );
    }

    return (
        <KeyboardCompatibleBottomSheet
            onDismiss={onClose}
            showCloseButton={false}
        >
            {content}
        </KeyboardCompatibleBottomSheet>
    );
};

interface EditAccountProps {
    account: AccountSummary;
    currentUserId: string;
    onClose: () => void;
    onPressRename?: () => void;
    onPressRecategorize?: () => void;
    onPressHide: () => void;
    onPressRelink?: () => void;
}

const EditAccount = ({
    account,
    currentUserId,
    onClose,
    onPressRename,
    onPressRecategorize,
    onPressHide,
    onPressRelink,
}: EditAccountProps) => {
    const {
        logo,
        name,
        institutionName,
        accountNumberMasked,
        lastUpdated,
        type,
        provider,
    } = account;

    const isManualAccount = account.provider === Provider.MANUAL;
    const isFruitfulBankAccount = // Excludes Fruitful Investment accounts
        institutionName === FRUITFUL_INSTITUTION_NAME &&
        account.provider === Provider.SYNCTERA;
    const isSpinwheelAccount = provider === Provider.SPINWHEEL;
    const isCurrentUserAccount = currentUserId === account.userId;

    return (
        <YStack paddingTop={12}>
            <BodyMediumEmphasized textAlign="center">
                Edit account
            </BodyMediumEmphasized>
            <View alignSelf="flex-end" marginTop={-34} marginBottom={20}>
                <CloseButton background="gray" onPress={onClose} />
            </View>
            <HolisticAccountLogo
                institutionName={institutionName}
                logo={logo}
                size="lg"
                showShadow
                provider={provider}
            />
            <H2Regular marginTop={12} marginBottom={8} textAlign="center">
                {name}
            </H2Regular>
            {(accountNumberMasked != null || type != null) && (
                <Private>
                    <FootnoteRegular color="$gray800" textAlign="center">
                        {getPrettyAccountType(account)}{' '}
                        {formatMaskedValue(accountNumberMasked)}
                    </FootnoteRegular>
                </Private>
            )}
            {lastUpdated != null && (
                <FootnoteRegular color="$gray800" textAlign="center">
                    Last updated:{' '}
                    {formatDistanceToNowStrict(new Date(lastUpdated), {
                        addSuffix: true,
                    })}
                </FootnoteRegular>
            )}
            {!isManualAccount && !isFruitfulBankAccount && (
                <YStack marginTop={32} gap={12}>
                    {onPressRename != null && (
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Rename"
                            onPress={onPressRename}
                            icon={EditIcon}
                        />
                    )}
                    {!isSpinwheelAccount &&
                        isCurrentUserAccount &&
                        onPressRelink != null && (
                            <Button
                                variant={ButtonVariant.SECONDARY}
                                size={ButtonSize.SMALL}
                                text="Reconnect"
                                onPress={onPressRelink}
                                icon={LinkIcon}
                            />
                        )}
                    {onPressRecategorize != null && (
                        <Button
                            variant={ButtonVariant.SECONDARY}
                            size={ButtonSize.SMALL}
                            text="Recategorize"
                            onPress={onPressRecategorize}
                            icon={Grid2x2Icon}
                        />
                    )}
                    <Button
                        variant={ButtonVariant.SECONDARY}
                        size={ButtonSize.SMALL}
                        text="Hide"
                        icon={TrashIcon}
                        onPress={onPressHide}
                        tone="danger"
                    />
                </YStack>
            )}
        </YStack>
    );
};

interface RenameAccountProps {
    account: AccountSummary;
    onClose: () => void;
    onSubmit: (rename: string) => Promise<void>;
}

const RenameAccount = ({ account, onClose, onSubmit }: RenameAccountProps) => {
    const { logo, name, institutionName, provider } = account;
    const [rename, setRename] = useState(name);

    const handleSubmit = () => {
        if (rename.trim() === '') {
            return;
        }
        if (rename.trim() === name) {
            onClose();
            return;
        }
        void onSubmit(rename);
    };

    return (
        <YStack alignItems="center" margin={16}>
            <BodyMediumEmphasized>Edit name</BodyMediumEmphasized>
            <View alignSelf="flex-end" marginTop={-26} marginBottom={20}>
                <CloseButton backgroundColor="$gray200" onPress={onClose} />
            </View>
            <HolisticAccountLogo
                institutionName={institutionName}
                logo={logo}
                size="lg"
                showShadow
                provider={provider}
            />
            <KeyboardCompatibleBottomSheetTextInput
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                editable
                autocomplete="none"
                maxLength={20} // This is the limit for Fruitful accounts
                returnKeyType="done"
                enablesReturnKeyAutomatically
                component={StyledH2Input}
                value={rename}
                onChangeText={(text: string) => {
                    setRename(text);
                }}
                onSubmitEditing={handleSubmit}
                marginTop={12}
            />
            {isWeb && (
                <View marginTop={32} width="100%">
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        tone="positive"
                        size={ButtonSize.LARGE}
                        text="Save"
                        onPress={handleSubmit}
                    />
                </View>
            )}
        </YStack>
    );
};

interface RecategorizeAccountProps {
    account: AccountSummary;
    onClose: () => void;
    onSubmit: (category: HolisticSummaryCategory) => Promise<void>;
}

const RecategorizeAccount = ({
    account,
    onClose,
    onSubmit,
}: RecategorizeAccountProps) => {
    const [selectedCategory, setSelectedCategory] = useState(account.category);

    return (
        <YStack alignItems="center" margin={16}>
            <BodyMediumEmphasized>Recategorize</BodyMediumEmphasized>
            <View alignSelf="flex-end" marginTop={-26} marginBottom={20}>
                <CloseButton backgroundColor="$gray200" onPress={onClose} />
            </View>
            <YStack width="100%">
                {[
                    AccountCategory.SPEND,
                    AccountCategory.SAVE,
                    AccountCategory.INVEST,
                    AccountCategory.BORROW,
                ].map((category) => (
                    <RadioInputRow
                        key={category}
                        label={capitalize(category)}
                        labelIcon={
                            AccountCategoryIcons[
                                category as HolisticSummaryCategory
                            ]
                        }
                        isSelected={selectedCategory === category}
                        onPress={() => {
                            setSelectedCategory(category);
                            void onSubmit(category as HolisticSummaryCategory);
                        }}
                    />
                ))}
            </YStack>
        </YStack>
    );
};

interface RecategorizeSpendProps {
    onClose: () => void;
    onSubmit: (spendAccountCategory: SpendAccountCategory) => Promise<void>;
}

const RecategorizeSpend = ({ onClose, onSubmit }: RecategorizeSpendProps) => {
    const [selectedCategory, setSelectedCategory] =
        useState<SpendAccountCategory | null>(null);

    return (
        <YStack alignItems="center" margin={16}>
            <BodyMediumEmphasized>Recategorize</BodyMediumEmphasized>
            <View alignSelf="flex-end" marginTop={-26} marginBottom={20}>
                <CloseButton backgroundColor="$gray200" onPress={onClose} />
            </View>
            <YStack width="100%">
                {Object.values(SpendAccountCategory).map((category) => (
                    <RadioInputRow
                        key={category}
                        label={capitalize(category)}
                        labelIcon={SpendCategoryIcons[category]}
                        isSelected={selectedCategory === category}
                        onPress={() => {
                            setSelectedCategory(category);
                            void onSubmit(category);
                        }}
                    />
                ))}
            </YStack>
        </YStack>
    );
};

interface ConfirmHideProps {
    isLastAccountOnLink: boolean;
    institutionName: string;
    onConfirmHide: () => Promise<void>;
    onGoBack: () => void;
}

const ConfirmHide = ({
    isLastAccountOnLink,
    institutionName,
    onConfirmHide,
    onGoBack,
}: ConfirmHideProps) => {
    return (
        <YStack
            alignItems="center"
            margin={16}
            paddingHorizontal={32}
            width="100%"
            maxWidth={500}
            alignSelf="center"
        >
            <BodyMediumRegular textAlign="center" marginVertical={12}>
                {isLastAccountOnLink
                    ? `This is your last visible account with ${institutionName}. Hiding this account will remove your link with ${institutionName}. Are you sure you want to proceed?`
                    : 'Are you sure you want to hide this account? The balance will be removed from all totals but the institution will not be unlinked.'}
            </BodyMediumRegular>
            <Button
                variant={ButtonVariant.TERTIARY}
                size={ButtonSize.LARGE}
                text="Go back"
                onPress={onGoBack}
            />
            <Button
                variant={ButtonVariant.PRIMARY}
                tone="danger"
                size={ButtonSize.LARGE}
                text={isLastAccountOnLink ? 'Remove link' : 'Hide this account'}
                onPress={onConfirmHide}
            />
        </YStack>
    );
};

export default EditAccountSheet;
